import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Package, AlertTriangle, TrendingDown, ArrowUpRight, ArrowDownRight,
  Plus, Minus, History, Settings, Search, RefreshCw, Save,
  XCircle, CheckCircle2, Clock, PackageOpen, Boxes, DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  good: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  low: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  reorder: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  out_of_stock: 'bg-red-500/10 text-red-600 border-red-500/20',
};

const STATUS_ICONS = {
  good: CheckCircle2, low: AlertTriangle, reorder: Clock, out_of_stock: XCircle,
};

function SummaryCards({ summary, t }) {
  const fmt = (v) => Number(v || 0).toLocaleString('ar-SA');
  const cards = [
    { label: t('inventory.totalItems'), value: fmt(summary.total_items), icon: Boxes, color: 'from-blue-500 to-indigo-600' },
    { label: t('inventory.lowStock'), value: fmt(summary.low_stock), icon: AlertTriangle, color: 'from-amber-500 to-orange-600' },
    { label: t('inventory.outOfStockCount'), value: fmt(summary.out_of_stock), icon: XCircle, color: 'from-red-500 to-rose-600' },
    { label: t('inventory.stockValue'), value: `${fmt(summary.total_stock_value)} ${t('common.sar')}`, icon: DollarSign, color: 'from-emerald-500 to-green-600' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="inventory-summary-cards">
      {cards.map((c, i) => (
        <Card key={i} className="border-0 shadow-sm overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">{c.label}</span>
              <div className={`p-1.5 rounded-lg bg-gradient-to-br ${c.color}`}>
                <c.icon className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
            <div className="text-xl font-bold">{c.value}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function OverviewTab({ items, onAdjust, onEditSettings, t, lang }) {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const filtered = items.filter(item => {
    const matchSearch = !search || item.name_ar?.includes(search) || item.name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || item.status === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-4" data-testid="inventory-overview-tab">
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder={t('common.search')} value={search} onChange={e => setSearch(e.target.value)}
            className="ps-9" data-testid="inventory-search" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[160px]" data-testid="inventory-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            <SelectItem value="good">{t('inventory.good')}</SelectItem>
            <SelectItem value="low">{t('inventory.low')}</SelectItem>
            <SelectItem value="reorder">{t('inventory.reorder')}</SelectItem>
            <SelectItem value="out_of_stock">{t('inventory.outOfStock')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        {filtered.map(item => {
          const StatusIcon = STATUS_ICONS[item.status] || Package;
          return (
            <Card key={item.id} className="border shadow-sm hover:shadow-md transition-shadow" data-testid={`inventory-item-${item.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {item.image_url ? (
                    <img src={item.image_url} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                      <Package className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold truncate">{lang === 'ar' ? item.name_ar : item.name}</span>
                      <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[item.status]}`}>
                        <StatusIcon className="h-3 w-3 me-1" />
                        {t(`inventory.${item.status === 'out_of_stock' ? 'outOfStock' : item.status}`)}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>{t('inventory.currentStock')}: <strong className="text-foreground">{item.current_stock}</strong> {t(`inventory.${item.unit}`)}</span>
                      <span>{t('inventory.minLevel')}: {item.min_stock_level}</span>
                      <span>{t('inventory.qtySoldMonth')}: {item.qty_sold_month}</span>
                      {item.days_until_out < 30 && (
                        <span className="text-amber-600">{t('inventory.daysUntilOut')}: {item.days_until_out}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button size="sm" variant="outline" className="h-8 gap-1"
                      onClick={() => onAdjust(item, 'add')} data-testid={`add-stock-${item.id}`}>
                      <Plus className="h-3.5 w-3.5" /><span className="hidden sm:inline">{t('inventory.addStock')}</span>
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 gap-1"
                      onClick={() => onAdjust(item, 'remove')} data-testid={`remove-stock-${item.id}`}>
                      <Minus className="h-3.5 w-3.5" /><span className="hidden sm:inline">{t('inventory.removeStock')}</span>
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
                      onClick={() => onEditSettings(item)} data-testid={`settings-${item.id}`}>
                      <Settings className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">{t('common.noData')}</div>
        )}
      </div>
    </div>
  );
}

function AlertsTab({ alerts, onAdjust, t }) {
  if (!alerts.length) {
    return (
      <div className="text-center py-16" data-testid="inventory-alerts-empty">
        <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500 mb-3" />
        <p className="text-muted-foreground">{t('inventory.noAlerts')}</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="inventory-alerts-tab">
      {alerts.map(a => (
        <Card key={a.id} className={`border-s-4 ${a.type === 'out_of_stock' ? 'border-s-red-500' : 'border-s-amber-500'}`}>
          <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              {a.type === 'out_of_stock' ? (
                <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
              )}
              <div>
                <span className="font-medium">{a.name}</span>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {t('inventory.currentStock')}: <strong>{a.stock}</strong> | {t('inventory.minLevel')}: {a.min_level}
                </div>
              </div>
            </div>
            <Button size="sm" onClick={() => onAdjust({ id: a.id, name_ar: a.name }, 'add')} data-testid={`restock-alert-${a.id}`}>
              <Plus className="h-3.5 w-3.5 me-1" />{t('inventory.restock')}
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function HistoryTab({ history, t, lang }) {
  if (!history.length) {
    return <div className="text-center py-16 text-muted-foreground" data-testid="inventory-history-empty">{t('inventory.noHistory')}</div>;
  }
  return (
    <div className="space-y-2" data-testid="inventory-history-tab">
      {history.map(h => (
        <Card key={h.id} className="border shadow-sm">
          <CardContent className="p-3 flex items-center gap-3">
            <div className={`p-2 rounded-full ${h.type === 'add' ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
              {h.type === 'add' ? <ArrowUpRight className="h-4 w-4 text-emerald-600" /> : <ArrowDownRight className="h-4 w-4 text-red-600" />}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">{h.item_name}</span>
                <Badge variant="outline" className="text-[10px]">
                  {h.quantity > 0 ? `+${h.quantity}` : h.quantity}
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {t(`inventory.${h.reason}`)} {h.notes && `- ${h.notes}`}
              </div>
            </div>
            <div className="text-end text-xs text-muted-foreground flex-shrink-0">
              <div>{h.user_name}</div>
              <div>{new Date(h.created_at).toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function InventoryPage() {
  const { activeBranch, authHeaders } = useAuth();
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Adjust dialog
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustItem, setAdjustItem] = useState(null);
  const [adjustType, setAdjustType] = useState('add');
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustReason, setAdjustReason] = useState('restock');
  const [adjustNotes, setAdjustNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Settings dialog
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsItem, setSettingsItem] = useState(null);
  const [minLevel, setMinLevel] = useState('10');
  const [reorderPoint, setReorderPoint] = useState('20');
  const [unit, setUnit] = useState('pcs');

  const fetchData = useCallback(async () => {
    if (!activeBranch) return;
    setLoading(true);
    try {
      const [ovRes, alRes, hiRes] = await Promise.all([
        axios.get(`${API}/api/inventory/overview?branch_id=${activeBranch.id}`, authHeaders()),
        axios.get(`${API}/api/inventory/alerts?branch_id=${activeBranch.id}`, authHeaders()),
        axios.get(`${API}/api/inventory/history?branch_id=${activeBranch.id}`, authHeaders()),
      ]);
      setOverview(ovRes.data);
      setAlerts(alRes.data.alerts || []);
      setHistory(hiRes.data.items || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [activeBranch, authHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdjust = (item, type) => {
    setAdjustItem(item);
    setAdjustType(type);
    setAdjustQty('');
    setAdjustReason(type === 'add' ? 'restock' : 'waste');
    setAdjustNotes('');
    setAdjustOpen(true);
  };

  const submitAdjust = async () => {
    const q = parseInt(adjustQty);
    if (!q || q <= 0) return;
    setSaving(true);
    try {
      const quantity = adjustType === 'add' ? q : -q;
      await axios.post(`${API}/api/inventory/adjust/${adjustItem.id}`, {
        branch_id: activeBranch.id, quantity, reason: adjustReason, notes: adjustNotes,
      }, authHeaders());
      toast.success(t('inventory.adjustSuccess'));
      setAdjustOpen(false);
      fetchData();
    } catch { toast.error(t('inventory.adjustFailed')); }
    finally { setSaving(false); }
  };

  const openSettings = (item) => {
    setSettingsItem(item);
    setMinLevel(String(item.min_stock_level || 10));
    setReorderPoint(String(item.reorder_point || 20));
    setUnit(item.unit || 'pcs');
    setSettingsOpen(true);
  };

  const submitSettings = async () => {
    try {
      await axios.put(`${API}/api/inventory/settings/${settingsItem.id}`, {
        min_stock_level: parseInt(minLevel) || 10,
        reorder_point: parseInt(reorderPoint) || 20,
        unit,
      }, authHeaders());
      toast.success(t('inventory.settingsUpdated'));
      setSettingsOpen(false);
      fetchData();
    } catch { toast.error(t('common.failed')); }
  };

  if (loading || !overview) {
    return (
      <ModuleLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        </div>
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout>
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="inventory-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
              <Package className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              {t('inventory.title')}
            </h1>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="inventory-refresh">
            <RefreshCw className="h-4 w-4 me-1" />{t('common.update')}
          </Button>
        </div>

        <SummaryCards summary={overview.summary} t={t} />

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex w-full overflow-x-auto no-scrollbar" data-testid="inventory-tabs">
            <TabsTrigger value="overview" className="gap-1 flex-shrink-0 text-xs sm:text-sm"><PackageOpen className="h-3.5 w-3.5 sm:h-4 sm:w-4" />{t('inventory.overview')}</TabsTrigger>
            <TabsTrigger value="alerts" className="gap-1 flex-shrink-0 text-xs sm:text-sm">
              <AlertTriangle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />{t('inventory.alerts')}
              {alerts.length > 0 && <Badge variant="destructive" className="ms-1 h-5 min-w-5 text-[10px]">{alerts.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-1 flex-shrink-0 text-xs sm:text-sm"><History className="h-3.5 w-3.5 sm:h-4 sm:w-4" />{t('inventory.history')}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <OverviewTab items={overview.items} onAdjust={openAdjust} onEditSettings={openSettings} t={t} lang={lang} />
          </TabsContent>
          <TabsContent value="alerts">
            <AlertsTab alerts={alerts} onAdjust={openAdjust} t={t} />
          </TabsContent>
          <TabsContent value="history">
            <HistoryTab history={history} t={t} lang={lang} />
          </TabsContent>
        </Tabs>

        {/* Adjust Stock Dialog */}
        <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
          <DialogContent data-testid="adjust-stock-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {adjustType === 'add' ? <Plus className="h-5 w-5 text-emerald-600" /> : <Minus className="h-5 w-5 text-red-600" />}
                {adjustType === 'add' ? t('inventory.addStock') : t('inventory.removeStock')}
                {adjustItem && ` - ${adjustItem.name_ar || adjustItem.name}`}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <label className="text-sm font-medium mb-1 block">{t('inventory.quantity')}</label>
                <Input type="number" min="1" value={adjustQty} onChange={e => setAdjustQty(e.target.value)}
                  data-testid="adjust-quantity-input" placeholder="0" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">{t('inventory.reason')}</label>
                <Select value={adjustReason} onValueChange={setAdjustReason}>
                  <SelectTrigger data-testid="adjust-reason-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {adjustType === 'add' ? (
                      <>
                        <SelectItem value="restock">{t('inventory.restock')}</SelectItem>
                        <SelectItem value="return">{t('inventory.return')}</SelectItem>
                        <SelectItem value="correction">{t('inventory.correction')}</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="waste">{t('inventory.waste')}</SelectItem>
                        <SelectItem value="correction">{t('inventory.correction')}</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">{t('inventory.notes')}</label>
                <Input value={adjustNotes} onChange={e => setAdjustNotes(e.target.value)}
                  data-testid="adjust-notes-input" />
              </div>
              <Button className="w-full" onClick={submitAdjust} disabled={saving || !adjustQty} data-testid="adjust-submit-btn">
                <Save className="h-4 w-4 me-2" />{t('common.save')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Settings Dialog */}
        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogContent data-testid="inventory-settings-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                {t('inventory.settings')} - {settingsItem?.name_ar || settingsItem?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <label className="text-sm font-medium mb-1 block">{t('inventory.minLevel')}</label>
                <Input type="number" min="0" value={minLevel} onChange={e => setMinLevel(e.target.value)} data-testid="settings-min-level" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">{t('inventory.reorderPoint')}</label>
                <Input type="number" min="0" value={reorderPoint} onChange={e => setReorderPoint(e.target.value)} data-testid="settings-reorder-point" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">{t('inventory.unit')}</label>
                <Select value={unit} onValueChange={setUnit}>
                  <SelectTrigger data-testid="settings-unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pcs">{t('inventory.pcs')}</SelectItem>
                    <SelectItem value="kg">{t('inventory.kg')}</SelectItem>
                    <SelectItem value="liter">{t('inventory.liter')}</SelectItem>
                    <SelectItem value="box">{t('inventory.box')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full" onClick={submitSettings} data-testid="settings-submit-btn">
                <Save className="h-4 w-4 me-2" />{t('common.save')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </ModuleLayout>
  );
}
